import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import { Input, Select } from '../components/ui/Input';
import { Tabs } from '../components/ui';
import WORLD from '../data/world-map.json';
import ExportSupplyArrangements from './ExportSupplyArrangements';

/*
 * Export Customers (migration 076) — Matthew, 2026-09-21:
 *
 *   "under the 'Customer & Growth' section, can we create a tab for export customers where
 *    we can view a map with table details below where we can visibility show all known
 *    customers."
 *
 * The map is drawn from app/src/data/world-map.json — real Natural Earth geometry, converted
 * once by scripts/build_world_map.py from world-atlas + the ISO code list. No mapping
 * library: the projection below is four lines of arithmetic, and pulling in
 * react-simple-maps + d3-geo + topojson-client for one page is not a trade worth making.
 *
 * ⚠ That file is the SINGLE source for the shapes, the country names and the picker on the
 * CRM form. One source means a country cannot be spelled one way in the table and another
 * on the map, and the picker cannot offer a country the map has no shape for.
 *
 * That last clause has teeth: on 2026-09-21 Matthew reported Bahrain, Hong Kong, Singapore
 * and the Maldives missing from the picker. They were missing because 1:110m Natural Earth
 * has no polygon for them — the rule working, not failing. The builder now takes each
 * country at the coarsest scale that HAS it (1:110m, filled from 1:50m), which is why the
 * count is 235 rather than 174. Fixing this by ADDING a country to the picker alone would
 * put a name in the dropdown that the map cannot draw; fix the asset instead.
 */

/** ISO alpha-2 -> {name, centroid, polygons}, built once at module load. */
export const COUNTRY_BY_CODE = Object.fromEntries(WORLD.countries.map((c) => [c.code, c]));
/** For the picker on the customer form — name-sorted, which is how people look for one. */
export const COUNTRY_OPTIONS = WORLD.countries.map((c) => ({ code: c.code, name: c.name }));

/*
 * Equirectangular: longitude and latitude map straight onto x and y. It stretches the poles
 * badly, which for a distribution map of trading nations is a fair trade for having no
 * projection code at all. Antarctica is clipped by MIN_LAT rather than drawn and ignored.
 */
const MIN_LAT = -60;
const MAX_LAT = 84;
const VB_W = 1000;
const VB_H = Math.round((VB_W * (MAX_LAT - MIN_LAT)) / 360);

const projX = (lon) => ((lon + 180) / 360) * VB_W;
const projY = (lat) => ((MAX_LAT - lat) / (MAX_LAT - MIN_LAT)) * VB_H;

const ringToPath = (ring) =>
  `M${ring.map(([lon, lat]) => `${projX(lon).toFixed(1)},${projY(lat).toFixed(1)}`).join('L')}Z`;

/** One <path> per country — built once, since the geometry never changes. */
const COUNTRY_PATHS = WORLD.countries.map((c) => ({
  code: c.code,
  name: c.name,
  d: c.p.map((poly) => poly.map(ringToPath).join('')).join(''),
}));

/*
 * The map's height is the viewer's to set (Matthew, 16 Oct: "drag the table below up into the
 * view which minimizes the size of the map area"). Remembered per browser only — a layout
 * preference, so localStorage, wrapped because it can be unavailable.
 */
const MAP_H_KEY = 'export-customers:map-height';
const MAP_H_MIN = 120;
const MAP_H_MAX = 900;
const MAP_H_DEFAULT = 420;
const readMapH = () => {
  try {
    const v = Number(localStorage.getItem(MAP_H_KEY));
    return v >= MAP_H_MIN && v <= MAP_H_MAX ? v : MAP_H_DEFAULT;
  } catch { return MAP_H_DEFAULT; }
};

const CARD = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 };
const TH = { textAlign: 'left', padding: '7px 10px', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' };
const TD = { padding: '7px 10px', fontSize: 12, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-soft)' };

/*
 * Two views (Matthew, 2026-10-16: "Add a tab on the pages to capture current export supply
 * arrangements"): where each customer distributes (076), and the supply deals we hold (080).
 */
export default function ExportCustomers() {
  const [tab, setTab] = useState('map');
  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: 0 }}>Export Customers</h2>
      </div>
      <Tabs value={tab} onChange={setTab} style={{ marginBottom: 14 }} items={[
        { id: 'map', label: 'Distribution map' },
        { id: 'supply', label: 'Supply arrangements' },
      ]} />
      {tab === 'map' ? <DistributionMap /> : <ExportSupplyArrangements />}
    </div>
  );
}

function DistributionMap() {
  const apiFetch = useApi();
  const [search, setSearch] = useState('');
  const [segFilter, setSegFilter] = useState('Export');
  const [selected, setSelected] = useState(null);   // a country code, or null for all
  const [mapH, setMapH] = useState(readMapH);
  const drag = useRef(null);

  // Pointer capture keeps the drag alive when the cursor leaves the handle.
  // The live height rides in the ref: the pointerup handler can close over a render that
  // predates the last move, so saving `mapH` from state stored a stale height.
  const onDragStart = (e) => {
    e.preventDefault();   // no text selection while dragging
    drag.current = { y: e.clientY, h: mapH, last: mapH };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onDragMove = (e) => {
    if (!drag.current) return;
    const h = Math.min(MAP_H_MAX, Math.max(MAP_H_MIN, drag.current.h + e.clientY - drag.current.y));
    drag.current.last = h;
    setMapH(h);
  };
  const onDragEnd = () => {
    if (!drag.current) return;
    const h = drag.current.last;
    drag.current = null;
    try { localStorage.setItem(MAP_H_KEY, String(h)); } catch { /* preference only */ }
  };

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ['customer-countries'],
    queryFn: async () => {
      const r = await apiFetch('/customer-countries');
      if (!r.ok) throw new Error(`Failed to load export countries: HTTP ${r.status}`);
      return r.json();
    },
  });

  const segments = useMemo(
    () => ['All', ...Array.from(new Set(rows.map((r) => r.segment).filter(Boolean))).sort()],
    [rows]);

  const scoped = useMemo(
    () => (segFilter === 'All' ? rows : rows.filter((r) => r.segment === segFilter)),
    [rows, segFilter]);

  /** code -> [{customerCode, customerName}], the "who goes here" the map is asking. */
  const byCountry = useMemo(() => {
    const m = new Map();
    for (const r of scoped) {
      const arr = m.get(r.countryCode) ?? [];
      arr.push(r);
      m.set(r.countryCode, arr);
    }
    return m;
  }, [scoped]);

  /** One row per customer, countries folded in — the table reads per account. */
  const customers = useMemo(() => {
    const m = new Map();
    for (const r of scoped) {
      const c = m.get(r.customerCode) ?? {
        customerCode: r.customerCode, customerName: r.customerName, segment: r.segment, countries: [],
      };
      c.countries.push(r.countryCode);
      m.set(r.customerCode, c);
    }
    const q = search.trim().toLowerCase();
    return [...m.values()]
      .filter((c) => !selected || c.countries.includes(selected))
      .filter((c) => !q
        || `${c.customerName} ${c.customerCode}`.toLowerCase().includes(q)
        || c.countries.some((k) => (COUNTRY_BY_CODE[k]?.name ?? k).toLowerCase().includes(q)))
      .sort((a, b) => a.customerName.localeCompare(b.customerName));
  }, [scoped, search, selected]);

  const maxPerCountry = useMemo(
    () => Math.max(1, ...[...byCountry.values()].map((v) => v.length)), [byCountry]);

  // Reached countries shade by how many customers cover them. Everything else stays the
  // land colour — an unreached country is not a zero, it is simply not part of this picture.
  const fillFor = (code) => {
    const n = byCountry.get(code)?.length ?? 0;
    if (!n) return 'var(--border)';
    const t = 0.35 + 0.65 * (n / maxPerCountry);
    return `color-mix(in srgb, var(--info) ${Math.round(t * 100)}%, var(--bg-elevated))`;
  };

  return (
    <div>
      {error && (
        <div style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6,
          color: 'var(--danger)', padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>
          {String(error.message || error)}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
            {customers.length} {customers.length === 1 ? 'customer' : 'customers'} · {byCountry.size}{' '}
            {byCountry.size === 1 ? 'country' : 'countries'}
            {selected && <> · filtered to <strong style={{ color: 'var(--info)' }}>{COUNTRY_BY_CODE[selected]?.name ?? selected}</strong></>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Customer or country…" style={{ width: 190, fontSize: 12 }} />
          <Select value={segFilter} onChange={(e) => setSegFilter(e.target.value)} style={{ fontSize: 12 }}>
            {segments.map((s2) => <option key={s2} value={s2}>{s2 === 'All' ? 'All channels' : s2}</option>)}
          </Select>
          {selected && (
            <button onClick={() => setSelected(null)}
              style={{ background: 'var(--info-soft)', border: '1px solid var(--info-border)', borderRadius: 6,
                color: 'var(--info)', padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>
              Clear country
            </button>
          )}
        </div>
      </div>

      {isLoading && <div style={{ color: 'var(--text-muted)', padding: 30, textAlign: 'center' }}>Loading…</div>}

      {!isLoading && rows.length === 0 && (
        <div style={{ ...CARD, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, padding: 30 }}>
          No distribution countries recorded yet. Open a customer in the CRM, set the channel to
          Export, and pick the countries they distribute to — they will appear here.
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <>
          <div style={{ ...CARD, marginBottom: 12, paddingBottom: 0 }}>
            <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="xMidYMid meet"
              style={{ width: '100%', height: mapH, display: 'block' }}
              role="img" aria-label="World map of export distribution countries">
              <rect x="0" y="0" width={VB_W} height={VB_H} fill="var(--bg-elevated)" />
              {COUNTRY_PATHS.map((c) => {
                const n = byCountry.get(c.code)?.length ?? 0;
                const isSel = selected === c.code;
                return (
                  <path
                    key={c.code}
                    d={c.d}
                    fill={fillFor(c.code)}
                    stroke={isSel ? 'var(--text-primary)' : 'var(--bg-elevated)'}
                    strokeWidth={isSel ? 1.6 : 0.4}
                    style={{ cursor: n ? 'pointer' : 'default' }}
                    onClick={() => n && setSelected(isSel ? null : c.code)}
                  >
                    {/* A native <title> is the tooltip — no tooltip library, and it is
                        what a screen reader reads out. */}
                    <title>{n ? `${c.name} — ${n} ${n === 1 ? 'customer' : 'customers'}` : c.name}</title>
                  </path>
                );
              })}
              {/* A dot on each reached country, so a small nation is still visible at this
                  scale — Singapore is a few pixels of fill and easy to miss. */}
              {[...byCountry.keys()].map((code) => {
                const c = COUNTRY_BY_CODE[code];
                if (!c) return null;
                return (
                  <circle key={code} cx={projX(c.c[0])} cy={projY(c.c[1])} r={selected === code ? 6 : 4}
                    fill="var(--info)" stroke="var(--bg-elevated)" strokeWidth="1"
                    style={{ cursor: 'pointer' }} onClick={() => setSelected(selected === code ? null : code)}>
                    <title>{c.name} — {byCountry.get(code).length}</title>
                  </circle>
                );
              })}
            </svg>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              Click a country to filter the table. Shading is how many customers cover it. Drag the bar below to resize the map.
            </div>
            <div role="separator" aria-orientation="horizontal" aria-label="Resize map" title="Drag to resize the map"
              onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd}
              onDoubleClick={() => { setMapH(MAP_H_DEFAULT); try { localStorage.removeItem(MAP_H_KEY); } catch { /* preference only */ } }}
              style={{ height: 14, margin: '6px -14px 0', cursor: 'ns-resize', touchAction: 'none', userSelect: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: '1px solid var(--border-soft)' }}>
              <div style={{ width: 48, height: 4, borderRadius: 2, background: 'var(--border-soft)' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 12 }}>
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--border)' }}>
                  {['Customer', 'Channel', 'Distributes to'].map((h) => <th key={h} style={TH}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {customers.length === 0 && (
                    <tr><td style={{ ...TD, color: 'var(--text-muted)' }} colSpan={3}>Nothing matches.</td></tr>
                  )}
                  {customers.map((c) => (
                    <tr key={c.customerCode}>
                      <td style={TD}>
                        <div>{c.customerName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.customerCode}</div>
                      </td>
                      <td style={{ ...TD, color: 'var(--text-muted)' }}>{c.segment || '—'}</td>
                      <td style={TD}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {c.countries
                            .slice()
                            .sort((a, b) => (COUNTRY_BY_CODE[a]?.name ?? a).localeCompare(COUNTRY_BY_CODE[b]?.name ?? b))
                            .map((k) => (
                              <span key={k} onClick={() => setSelected(selected === k ? null : k)}
                                style={{ background: selected === k ? 'var(--info)' : 'var(--info-soft)',
                                  color: selected === k ? '#fff' : 'var(--info)', borderRadius: 10,
                                  padding: '1px 8px', fontSize: 11, cursor: 'pointer' }}>
                                {COUNTRY_BY_CODE[k]?.name ?? k}
                              </span>
                            ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--border)' }}>
                  {['Country', 'Customers'].map((h) => <th key={h} style={TH}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {[...byCountry.entries()]
                    .sort((a, b) => b[1].length - a[1].length
                      || (COUNTRY_BY_CODE[a[0]]?.name ?? a[0]).localeCompare(COUNTRY_BY_CODE[b[0]]?.name ?? b[0]))
                    .map(([code, list]) => (
                      <tr key={code} onClick={() => setSelected(selected === code ? null : code)}
                        style={{ cursor: 'pointer', background: selected === code ? 'var(--info-soft)' : 'transparent' }}>
                        <td style={TD}>{COUNTRY_BY_CODE[code]?.name ?? code}</td>
                        <td style={{ ...TD, textAlign: 'right', fontWeight: 600 }}>{list.length}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
